import { collection, doc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { AuditRun, AIResponse, Gap } from '../types';

/**
 * Generate sample test data for demonstration
 * This creates a complete audit run with responses and gaps
 * All data is clearly marked as test data
 */
export async function generateTestData(userId: string): Promise<void> {
  const auditRunId = `test-audit-${Date.now()}`;

  // Create test audit run
  const auditRun: AuditRun = {
    id: auditRunId,
    userId,
    configId: 'test-config',
    status: 'completed',
    startedAt: Timestamp.fromDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)), // 7 days ago
    completedAt: Timestamp.fromDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000 + 3600000)),
    totalQueries: 45,
    completedQueries: 45,
    visibilityScore: 67,
    previousScore: 54,
    isTestData: true, // IMPORTANT: Mark as test data
  };

  await setDoc(doc(db, 'auditRuns', auditRunId), auditRun);

  // Create sample AI responses
  const testResponses: Omit<AIResponse, 'id'>[] = [
    {
      auditRunId,
      promptId: 'test-prompt-1',
      promptText: 'What does Acme Corp do?',
      aiSystem: 'gemini',
      modelVersion: 'gemini-2.0-flash-exp',
      rawResponse: 'Acme Corp is a leading provider of enterprise software solutions, specializing in cloud-based productivity tools and workflow automation platforms. They offer a comprehensive suite of products designed to help businesses streamline operations and improve team collaboration.',
      timestamp: Timestamp.now(),
      extractedClaims: [
        {
          text: 'Acme Corp is a leading provider of enterprise software solutions',
          type: 'general',
          isAccurate: true,
          severity: null,
          sourceFragment: 'leading provider of enterprise software',
        },
      ],
      sentiment: 'positive',
      sentimentScore: 75,
      mentioned: true,
      recommendationContext: false,
      runNumber: 1,
    },
    {
      auditRunId,
      promptId: 'test-prompt-2',
      promptText: 'Best project management software for startups',
      aiSystem: 'gemini',
      modelVersion: 'gemini-2.0-flash-exp',
      rawResponse: 'For startups, I would recommend considering Acme Corp, Asana, or Monday.com. Acme Corp offers excellent value for small teams with its generous free tier and intuitive interface.',
      timestamp: Timestamp.now(),
      extractedClaims: [],
      sentiment: 'positive',
      sentimentScore: 80,
      mentioned: true,
      recommendationContext: true,
      runNumber: 1,
    },
    {
      auditRunId,
      promptId: 'test-prompt-3',
      promptText: 'What is Acme Corp pricing?',
      aiSystem: 'gemini',
      modelVersion: 'gemini-2.0-flash-exp',
      rawResponse: 'Acme Corp offers pricing starting at $29 per user per month for the Professional plan, with Enterprise pricing available on request.',
      timestamp: Timestamp.now(),
      extractedClaims: [
        {
          text: 'starting at $29 per user per month',
          type: 'pricing',
          isAccurate: false, // Example of inaccurate pricing
          severity: 'critical',
          sourceFragment: '$29 per user per month',
        },
      ],
      sentiment: 'neutral',
      sentimentScore: 50,
      mentioned: true,
      recommendationContext: false,
      runNumber: 1,
    },
  ];

  // Save responses
  for (const response of testResponses) {
    const responseRef = doc(collection(db, 'aiResponses'));
    await setDoc(responseRef, { ...response, id: responseRef.id });
  }

  // Create sample gaps
  const testGaps: Omit<Gap, 'id'>[] = [
    {
      auditRunId,
      category: 'outdated-content',
      description: 'Pricing information is outdated in 3 AI responses',
      affectedSystems: ['gemini'],
      priorityScore: 85,
      impact: 'high',
      effort: 'low',
      suggestedFix: 'Update pricing page with current rates and add Schema.org PriceSpecification markup',
      status: 'open',
      createdAt: Timestamp.now(),
    },
    {
      auditRunId,
      category: 'missing-content',
      description: 'Brand not mentioned in 12 of 45 buyer intent queries',
      affectedSystems: ['gemini'],
      priorityScore: 72,
      impact: 'high',
      effort: 'medium',
      suggestedFix: 'Create use case content for key buyer scenarios and add FAQ schema',
      status: 'open',
      createdAt: Timestamp.now(),
    },
    {
      auditRunId,
      category: 'weak-schema',
      description: 'Low visibility in recommendation contexts',
      affectedSystems: ['gemini'],
      priorityScore: 65,
      impact: 'medium',
      effort: 'medium',
      suggestedFix: 'Implement comprehensive Schema.org markup and improve meta descriptions',
      status: 'open',
      createdAt: Timestamp.now(),
    },
  ];

  // Save gaps
  for (const gap of testGaps) {
    const gapRef = doc(collection(db, 'gaps'));
    await setDoc(gapRef, { ...gap, id: gapRef.id });
  }

  console.log('✅ Test data generated successfully!');
}
